import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormStore } from 'simple-creator/stores/formStore';
import { useFormContext } from 'simple-creator/contexts/FormContext';
import { useBalloonEventsMetadata, useUser } from 'react-ui';
import type { Tag } from 'ts-core';
import { TagSelector } from '../inputs/TagSelector';
import { TextContentSection } from '../inputs/TextContentSection';
import { ContactInfoForm } from '../inputs/ContactInfoForm';
import { OrganizerSection } from '../inputs/OrganizerSection';
import type { DescriptionEntry, ContactInfoEntry, OrganiserEntry } from 'simple-creator/types';

const DETAILS_STEP_INDEX = 3;

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, defaultOpen = false, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section className="bg-gray-50 rounded-2xl">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none border-none focus:ring-0 focus:border-none focus:ring-offset-0"
        aria-expanded={isOpen}
      >
        <span className="text-base font-semibold text-gray-900">{title}</span>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && <div className="px-5 pb-5">{children}</div>}
    </section>
  );
};

export const DetailsStep: React.FC = () => {
  const { formData, updateField, stepValidation, setStepValidation } = useFormStore();
  const { handleNextStep } = useFormContext();
  const { user } = useUser();
  const { metadata } = useBalloonEventsMetadata();

  const canViewSystemTags = user?.hasPermission('view-tags-index') ?? false;

  const systemTagSlugs = useMemo(() => {
    const tags: Tag[] = metadata?.tags ?? [];
    return new Set(tags.filter((tag: Tag) => tag.type === 'system').map((tag: Tag) => tag.slug));
  }, [metadata]);

  useEffect(() => {
    if (canViewSystemTags || systemTagSlugs.size === 0 || !formData.tags?.length) return;

    const filteredTags = formData.tags.filter((slug) => !systemTagSlugs.has(slug));
    if (filteredTags.length !== formData.tags.length) {
      updateField('tags', filteredTags);
    }
  }, [canViewSystemTags, formData.tags, systemTagSlugs, updateField]);

  const canProceed = stepValidation[DETAILS_STEP_INDEX] !== false;

  const handleValidationChange = useCallback((isValid: boolean) => {
    setStepValidation(DETAILS_STEP_INDEX, isValid);
  }, [setStepValidation]);

  const handleSkip = () => {
    if (!canProceed) return;
    handleNextStep(); // Saves draft before navigating
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center mb-6">
        <p className="text-gray-600 mb-4">
          Add more details to make your event stand out (all optional)
        </p>
        <button
          type="button"
          onClick={handleSkip}
          disabled={!canProceed}
          className={`px-8 py-3 border-2 rounded-full font-medium transition-colors text-base ${
            canProceed
              ? 'text-balloon border-balloon hover:bg-balloon hover:text-white'
              : 'text-gray-400 border-gray-300 cursor-not-allowed'
          }`}
        >
          Skip this step →
        </button>
        {!canProceed && (
          <p className="text-red-500 text-sm mt-2">Please fix validation errors before continuing</p>
        )}
      </div>

      {/* Tags Section — expanded by default */}
      <CollapsibleSection title="Tags" defaultOpen>
        <p className="text-sm text-gray-500 mb-3">
          Select tags to help people find your event
        </p>
        <TagSelector
          value={formData.tags || []}
          onChange={(tags) => updateField('tags', tags)}
          showSystemTags={canViewSystemTags}
        />
      </CollapsibleSection>

      {/* Text Content Section */}
      <CollapsibleSection title="Text Content">
        <TextContentSection
          subtitle={formData.subtitle || ''}
          onSubtitleChange={(subtitle) => updateField('subtitle', subtitle)}
          descriptions={formData.descriptions || []}
          onDescriptionsChange={(descriptions: DescriptionEntry[]) => updateField('descriptions', descriptions)}
        />
      </CollapsibleSection>

      {/* Contact Information Section */}
      <CollapsibleSection title="Contact Information">
        <ContactInfoForm
          contacts={formData.contactInfos || []}
          onChange={(contactInfos: ContactInfoEntry[]) => updateField('contactInfos', contactInfos)}
          onValidationChange={handleValidationChange}
        />
      </CollapsibleSection>

      {/* Organizer Section */}
      <CollapsibleSection title="Organizer">
        <OrganizerSection
          organisers={formData.organisers || []}
          onChange={(organisers: OrganiserEntry[]) => updateField('organisers', organisers)}
        />
      </CollapsibleSection>
    </div>
  );
};
